<template>
  <div
    v-if="embedUrl"
    class="zoom-embed-widget"
    :class="{
      'zoom-embed-widget--menu': anchoredToMenu,
      'zoom-embed-widget--expanded': isExpanded,
      'zoom-embed-widget--short': shortViewport
    }"
    :style="widgetStyle"
  >
    <button
      v-show="!isExpanded"
      type="button"
      class="zoom-embed-launcher"
      aria-label="Open Zoom"
      title="Open Zoom"
      @click="expand"
    >
      <feather-icon
        icon="VideoIcon"
        size="22"
      />
    </button>

    <div
      class="zoom-embed-panel"
      :class="{ 'zoom-embed-panel--collapsed': !isExpanded }"
      :style="isExpanded ? panelStyle : null"
      :aria-hidden="!isExpanded"
      role="dialog"
      aria-label="Zoom Contact Center"
    >
      <div
        v-show="isExpanded"
        class="zoom-embed-resize-handle"
        aria-hidden="true"
        title="Drag to resize"
        @mousedown.prevent="startResize"
      />

      <div
        v-show="isExpanded"
        class="zoom-embed-header"
      >
        <span class="zoom-embed-title">Zoom</span>
        <button
          type="button"
          class="zoom-embed-minimize"
          aria-label="Minimize Zoom"
          title="Minimize"
          @click="collapse"
        >
          <feather-icon
            icon="MinusIcon"
            size="16"
          />
        </button>
      </div>

      <div class="zoom-embed-body">
        <iframe
          :id="iframeId"
          :src="embedUrl"
          class="zoom-embed-iframe"
          title="Zoom Contact Center"
          :sandbox="iframeSandbox"
          :allow="iframeAllow"
          allowfullscreen
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  ZOOM_EMBED_EXPAND_EVENT,
  ZOOM_EMBED_IFRAME_ALLOW,
  ZOOM_EMBED_IFRAME_ID,
  ZOOM_EMBED_IFRAME_SANDBOX,
  ZOOM_PANEL_HEADER_HEIGHT
} from '@/constants/zoomEmbed'
import {
  getZoomEmbedPreferences,
  saveZoomEmbedPreferences,
  clampZoomSize,
  DEFAULT_SIZE,
  MIN_SIZE
} from '@/utils/zoomEmbedStorage'
import {
  buildZoomEmbedUrl,
  isZoomEmbedOrigin,
  parseZoomEmbedResize,
  parseZoomInboundCallConnected,
  logZoomEmbedEngagement
} from '@/utils/zoomEmbedMessage'
import ZoomProvider from '@/resources/ZoomProvider'
import { showErrorToast } from '@/utils/toast'

const ZoomService = new ZoomProvider()

const PANEL_OFFSET = 24
const PANEL_GAP = 10
const LAUNCHER_SIZE = 56
const MENU_ANCHOR_ID = 'main-menu-navigation'
const HANDLED_ENGAGEMENT_CACHE_LIMIT = 50
/** Below this height, pin expanded Zoom higher / tighter to save vertical space */
const SHORT_VIEWPORT_HEIGHT = 800

export default {
  name: 'ZoomEmbedWidget',
  data () {
    const prefs = getZoomEmbedPreferences()

    return {
      embedUrl: buildZoomEmbedUrl(),
      isExpanded: prefs.isExpanded,
      width: prefs.width || DEFAULT_SIZE.width,
      height: prefs.height || DEFAULT_SIZE.height,
      isResizing: false,
      iframeId: ZOOM_EMBED_IFRAME_ID,
      iframeSandbox: ZOOM_EMBED_IFRAME_SANDBOX,
      iframeAllow: ZOOM_EMBED_IFRAME_ALLOW,
      handledEngagementIds: new Set(),
      menuAnchor: null
    }
  },
  computed: {
    anchoredToMenu () {
      return Boolean(this.menuAnchor)
    },
    shortViewport () {
      return typeof window !== 'undefined' && window.innerHeight < SHORT_VIEWPORT_HEIGHT
    },
    widgetStyle () {
      if (!this.menuAnchor) return null

      return {
        top: `${this.resolvedMenuTop}px`,
        right: `${this.menuAnchor.right}px`,
        bottom: 'auto'
      }
    },
    /**
     * On short screens, reduce top so the expanded panel stays in view
     * instead of hanging from the menu center and clipping the bottom.
     */
    resolvedMenuTop () {
      if (!this.menuAnchor) return PANEL_OFFSET

      const menuTop = this.menuAnchor.top
      if (!this.isExpanded) return menuTop

      const gap = this.shortViewport ? 4 : PANEL_GAP
      const maxPanelBottom = window.innerHeight - PANEL_OFFSET
      // Expanded: launcher is hidden so panel offset is just `gap` from widget top
      const panelTop = menuTop + gap
      const overflow = panelTop + this.height - maxPanelBottom
      if (overflow <= 0) return menuTop

      // Pull the whole float up (lower top) so the panel fits
      return Math.max(PANEL_OFFSET, Math.round(menuTop - overflow))
    },
    panelStyle () {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`
      }
    }
  },
  mounted () {
    window.addEventListener('message', this.onZoomEmbedMessage)
    window.addEventListener(ZOOM_EMBED_EXPAND_EVENT, this.expand)
    document.addEventListener('mousemove', this.onResizeMove)
    document.addEventListener('mouseup', this.stopResize)
    window.addEventListener('resize', this.onViewportChange)
    window.addEventListener('scroll', this.syncMenuAnchor, true)
    this.$nextTick(() => {
      this.syncMenuAnchor()
      this.fitPanelToViewport()
    })
    // Menu may mount after App; retry briefly then stop
    let tries = 0
    this._anchorRetry = window.setInterval(() => {
      this.syncMenuAnchor()
      tries += 1
      if (this.menuAnchor || tries >= 20) {
        window.clearInterval(this._anchorRetry)
        this._anchorRetry = null
      }
    }, 250)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.onZoomEmbedMessage)
    window.removeEventListener(ZOOM_EMBED_EXPAND_EVENT, this.expand)
    document.removeEventListener('mousemove', this.onResizeMove)
    document.removeEventListener('mouseup', this.stopResize)
    window.removeEventListener('resize', this.onViewportChange)
    window.removeEventListener('scroll', this.syncMenuAnchor, true)
    if (this._anchorRetry) {
      window.clearInterval(this._anchorRetry)
      this._anchorRetry = null
    }
  },
  methods: {
    /**
     * Float over the far-right of the horizontal menu bar when visible;
     * otherwise keep the classic bottom-right floating position.
     */
    syncMenuAnchor () {
      const menu = document.getElementById(MENU_ANCHOR_ID)
      if (!menu) {
        this.menuAnchor = null
        return
      }

      // Full menu bar width — button sits at the far right of the bar
      const bar = menu.closest('.main-menu-content') || menu
      const rect = bar.getBoundingClientRect()
      const visible = rect.width > 0
        && rect.height > 0
        && rect.bottom > 0
        && rect.top < window.innerHeight

      if (!visible) {
        this.menuAnchor = null
        return
      }

      this.menuAnchor = {
        top: Math.round(rect.top + (rect.height - LAUNCHER_SIZE) / 2),
        right: Math.max(PANEL_OFFSET, Math.round(window.innerWidth - rect.right))
      }
    },
    onViewportChange () {
      this.syncMenuAnchor()
      this.fitPanelToViewport()
    },
    /**
     * Keep expanded panel inside the viewport (short vertical resolutions).
     */
    fitPanelToViewport () {
      if (!this.isExpanded) return

      const maxWidth = window.innerWidth - PANEL_OFFSET * 2
      // Allow reduced top (down to PANEL_OFFSET) when clamping height
      let maxHeight = window.innerHeight - PANEL_OFFSET * 2

      if (this.menuAnchor) {
        const gap = this.shortViewport ? 4 : PANEL_GAP
        const panelTopMin = PANEL_OFFSET + gap
        maxHeight = Math.max(
          MIN_SIZE.height,
          window.innerHeight - PANEL_OFFSET - panelTopMin
        )
      }

      const nextSize = clampZoomSize(this.width, this.height, maxWidth, maxHeight)
      if (nextSize.width !== this.width || nextSize.height !== this.height) {
        this.width = nextSize.width
        this.height = nextSize.height
        this.persist()
      }
    },
    expand () {
      this.isExpanded = true
      this.persist()
      this.$nextTick(() => {
        this.syncMenuAnchor()
        this.fitPanelToViewport()
      })
    },
    collapse () {
      this.isExpanded = false
      this.persist()
    },
    persist () {
      saveZoomEmbedPreferences({
        isExpanded: this.isExpanded,
        width: this.width,
        height: this.height
      })
    },
    onZoomEmbedMessage (event) {
      if (!isZoomEmbedOrigin(event.origin)) return

      const skipOrigin = { skipOriginCheck: true }
      logZoomEmbedEngagement(event, skipOrigin)

      const inboundCall = parseZoomInboundCallConnected(event, skipOrigin)
      if (inboundCall) {
        this.openTicketFromInboundCall(inboundCall)
      }

      const resize = parseZoomEmbedResize(event, skipOrigin)
      if (!resize) return

      if (!this.isExpanded) {
        this.isExpanded = true
      }

      const maxWidth = window.innerWidth - PANEL_OFFSET * 2
      const maxHeight = window.innerHeight - PANEL_OFFSET * 2

      const nextSize = clampZoomSize(
        resize.width,
        resize.height + ZOOM_PANEL_HEADER_HEIGHT,
        maxWidth,
        maxHeight
      )

      this.width = nextSize.width
      this.height = nextSize.height
      this.persist()
      this.$nextTick(() => this.fitPanelToViewport())
    },
    startResize (event) {
      this.isResizing = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: this.width,
        startHeight: this.height
      }
    },
    onResizeMove (event) {
      if (!this.isResizing) return

      // bottom-right float: grow toward top-left
      // menu-anchored float: grow toward bottom-left
      const deltaX = this.isResizing.startX - event.clientX
      const deltaY = this.anchoredToMenu
        ? event.clientY - this.isResizing.startY
        : this.isResizing.startY - event.clientY
      const maxWidth = window.innerWidth - PANEL_OFFSET * 2
      let maxHeight = window.innerHeight - PANEL_OFFSET * 2
      if (this.menuAnchor) {
        const gap = this.shortViewport ? 4 : PANEL_GAP
        const panelTopMin = PANEL_OFFSET + gap
        maxHeight = Math.max(MIN_SIZE.height, window.innerHeight - PANEL_OFFSET - panelTopMin)
      }

      const nextSize = clampZoomSize(
        this.isResizing.startWidth + deltaX,
        this.isResizing.startHeight + deltaY,
        maxWidth,
        maxHeight
      )

      this.width = nextSize.width
      this.height = nextSize.height
    },
    stopResize () {
      if (!this.isResizing) return
      this.isResizing = false
      this.persist()
    },
    /**
     * Inbound call answered — trigger the same ANI screen-pop flow as CXone:
     * API matches customer, creates the ticket, and pushes it back over the
     * agent socket (toast + ticket store handled in App.vue).
     */
    async openTicketFromInboundCall ({ ani, skillName, engagementId }) {
      if (engagementId && this.handledEngagementIds.has(engagementId)) return

      // Reserve engagement id up front to block concurrent duplicate posts
      if (engagementId) {
        this.handledEngagementIds.add(engagementId)
        if (this.handledEngagementIds.size > HANDLED_ENGAGEMENT_CACHE_LIMIT) {
          const oldest = this.handledEngagementIds.values().next().value
          this.handledEngagementIds.delete(oldest)
        }
      }

      try {
        await ZoomService.callConnected({
          ani,
          skillName,
          interactionId: engagementId
        })
      } catch (error) {
        if (engagementId) {
          this.handledEngagementIds.delete(engagementId)
        }
        console.error('Zoom inbound ticket error:', error)
        showErrorToast(
          this,
          'Zoom inbound call',
          'Could not open a ticket for the incoming call. Please create it manually.'
        )
      }
    }
  }
}
</script>

<style scoped lang="scss">
.zoom-embed-widget {
  position: fixed;
  right: 24px;
  bottom: 24px;
  // Above horizontal menu (990), below header navbar (~997–999)
  // so the launcher stays visible but profile dropdown still wins
  z-index: 995;
}

.zoom-embed-widget--expanded {
  z-index: 1060;
}

.zoom-embed-launcher {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 50%;
  background: #2d8cff;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  }

  &:focus {
    outline: 2px solid #2d8cff;
    outline-offset: 2px;
  }
}

.zoom-embed-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.zoom-embed-widget--menu .zoom-embed-panel:not(.zoom-embed-panel--collapsed) {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
}

/* Short vertical viewport: hang panel closer (lower top offset) */
.zoom-embed-widget--menu.zoom-embed-widget--short .zoom-embed-panel:not(.zoom-embed-panel--collapsed) {
  top: 4px;
}

.zoom-embed-panel--collapsed {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  box-shadow: none;
}

.zoom-embed-widget--menu .zoom-embed-panel--collapsed {
  top: 0;
  bottom: auto;
}

.zoom-embed-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    width: 10px;
    height: 10px;
    border-top: 2px solid #b4b7bd;
    border-left: 2px solid #b4b7bd;
    border-top-left-radius: 2px;
  }
}

.zoom-embed-widget--menu .zoom-embed-resize-handle {
  top: auto;
  bottom: 0;
  cursor: nesw-resize;

  &::before {
    top: auto;
    bottom: 4px;
    border-top: 0;
    border-bottom: 2px solid #b4b7bd;
    border-top-left-radius: 0;
    border-bottom-left-radius: 2px;
  }
}

.zoom-embed-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0 12px 0 24px;
  border-bottom: 1px solid #ebe9f1;
  background: #f8f8f8;
}

.zoom-embed-widget--short .zoom-embed-header {
  min-height: 36px;
  padding: 0 8px 0 18px;
}

.zoom-embed-title {
  font-size: 14px;
  font-weight: 600;
  color: #5e5873 !important;
}

.zoom-embed-minimize {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #6e6b7b;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: 2px solid #2d8cff;
    outline-offset: 1px;
  }
}

.zoom-embed-body {
  flex: 1;
  min-height: 0;
  background: #101010;
}

.zoom-embed-iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
